// Derived cell → entity index (not part of SimState, never hashed or saved). Rebuilt for every command and
// every tick (63 cells + entities: microseconds), then kept in sync by the floor/building helpers that move
// things around, so a stale index cannot outlive one dispatch or one tick. M3's topology.ts extends it.
import { cellIndex, inGrid, inRect, type Rect } from './core/grid.ts';
import type { Cell, Int } from './core/types.ts';
import type { SimDefs } from './defs.ts';
import type { SimState } from './state.ts';

export interface Occ {
  cols: Int;
  rows: Int;
  /** The current expansion mask (cells outside it are rock). */
  mask: Rect;
  /** Entity id per cell (row-major), 0 = empty. */
  ids: Int[];
}

/** occAt result for cells outside the grid or the current mask. */
export const ROCK = -1;

export function maskOf(s: SimState, d: SimDefs): Rect {
  const e = d.board.expansions[s.bounds];
  if (!e) throw new RangeError(`bounds ${s.bounds} has no expansion`);
  return { x0: e.x0, y0: e.y0, x1: e.x1, y1: e.y1 };
}

export function buildOcc(s: SimState, d: SimDefs): Occ {
  const cols = d.board.cols;
  const rows = d.board.rows;
  const ids: Int[] = [];
  for (let i = 0; i < cols * rows; i++) ids.push(0);
  const occ: Occ = { cols, rows, mask: maskOf(s, d), ids };
  for (const b of s.buildings) occ.ids[cellIndex(cols, b.x, b.y)] = b.id;
  for (const it of s.floor) occ.ids[cellIndex(cols, it.x, it.y)] = it.id;
  return occ;
}

/** Inside the grid and the current mask. */
export function isOpen(o: Occ, x: Int, y: Int): boolean {
  return inGrid(o.cols, o.rows, x, y) && inRect(o.mask, x, y);
}

/** Entity id at (x, y), 0 when empty, ROCK outside the grid or mask. */
export function occAt(o: Occ, x: Int, y: Int): Int {
  if (!isOpen(o, x, y)) return ROCK;
  return o.ids[cellIndex(o.cols, x, y)] as Int;
}

export function isFree(o: Occ, x: Int, y: Int): boolean {
  return occAt(o, x, y) === 0;
}

export function occSet(o: Occ, x: Int, y: Int, id: Int): void {
  o.ids[cellIndex(o.cols, x, y)] = id;
}

const BFS_DX: readonly Int[] = [0, 1, 0, -1];
const BFS_DY: readonly Int[] = [-1, 0, 1, 0];

/**
 * Nearest free open cell to (x, y) by BFS over open cells, neighbours in N/E/S/W order (deterministic:
 * the same board always yields the same cell). `claimed[cellIndex]` marks cells already promised to
 * earlier items of the same operation. (x, y) itself wins when free. Null when the board is full.
 */
export function nearestFree(o: Occ, x: Int, y: Int, claimed: readonly boolean[] = []): Cell | null {
  if (!isOpen(o, x, y)) return null;
  const seen: boolean[] = [];
  const qx: Int[] = [x];
  const qy: Int[] = [y];
  seen[cellIndex(o.cols, x, y)] = true;
  for (let h = 0; h < qx.length; h++) {
    const cx = qx[h] as Int;
    const cy = qy[h] as Int;
    const ci = cellIndex(o.cols, cx, cy);
    if (o.ids[ci] === 0 && claimed[ci] !== true) return { x: cx, y: cy };
    for (let k = 0; k < 4; k++) {
      const nx = cx + (BFS_DX[k] as Int);
      const ny = cy + (BFS_DY[k] as Int);
      if (!isOpen(o, nx, ny)) continue;
      const ni = cellIndex(o.cols, nx, ny);
      if (seen[ni] === true) continue;
      seen[ni] = true;
      qx.push(nx);
      qy.push(ny);
    }
  }
  return null;
}
